import { defineStore } from 'pinia'
import {
  GMAX_DAILY_PARTICLES,
  GMAX_GUARANTEED_FRAGMENTS,
  GMAX_PARTICLE_CAP,
  GMAX_PITY_REQUIRED,
  GMAX_RAIDS,
  GMAX_STARTING_PARTICLES,
  estimateGmaxCatchRate,
  getGmaxRaid,
  type GmaxRaidDefinition,
} from '~/data/gmaxRaids'
import { useInventoryStore } from '~/stores/useInventoryStore'
import { getRarity } from '~/data/gacha'
import { POKEDEX } from '~/data/pokedex'
import { getDisplayRarity, type DisplayRarity } from '~/data/pokemonPower'

export interface GmaxRaidProgress {
  fragments: number
  pity: number
  attempts: number
  clears: number
  captures: number
}

export interface GmaxRaidResult {
  raid: GmaxRaidDefinition
  won: boolean
  captured: boolean
  catchRate: number
  fragmentsEarned: number
  fragments: number
  pity: number
  pityTriggered: boolean
  fragmentsTriggered: boolean
  starUpgraded: boolean
  alreadyMaxed: boolean
  teamDps: number
}

export interface GmaxRaidPersistedState {
  particles?: number
  lastDailyRefresh?: string | null
  selectedSlug?: string | null
  progressBySlug?: Record<string, Partial<GmaxRaidProgress>>
}

function todayKey(date = new Date()): string {
  return date.toISOString().slice(0, 10)
}

function clampInt(value: unknown, min: number, max: number): number {
  const n = Math.floor(Number(value) || 0)
  return Math.max(min, Math.min(max, n))
}

function emptyProgress(): GmaxRaidProgress {
  return {
    fragments: 0,
    pity: 0,
    attempts: 0,
    clears: 0,
    captures: 0,
  }
}

function normalizeProgress(raw: Partial<GmaxRaidProgress> | undefined): GmaxRaidProgress {
  return {
    fragments: clampInt(raw?.fragments, 0, 999),
    pity: clampInt(raw?.pity, 0, 99),
    attempts: clampInt(raw?.attempts, 0, 999_999),
    clears: clampInt(raw?.clears, 0, 999_999),
    captures: clampInt(raw?.captures, 0, 999_999),
  }
}

function getBasePokemonName(slug: string): { nameFr: string; nameEn: string } {
  const entry = POKEDEX.find((pokemon) => pokemon.slug === slug)
  return {
    nameFr: entry?.nameFr ?? slug,
    nameEn: entry?.nameEn ?? slug,
  }
}

function gmaxRewardRarity(raid: GmaxRaidDefinition): DisplayRarity {
  const baseRarity = getRarity(raid.baseSlug)
  return getDisplayRarity({ slug: raid.baseSlug, cosmeticFormSlug: raid.slug, rarity: baseRarity })
}

export const useGmaxRaidStore = defineStore('gmaxRaids', {
  state: () => ({
    particles: GMAX_STARTING_PARTICLES,
    lastDailyRefresh: null as string | null,
    selectedSlug: GMAX_RAIDS[0]?.slug ?? null,
    progressBySlug: {} as Record<string, GmaxRaidProgress>,
    lastResult: null as GmaxRaidResult | null,
  }),

  getters: {
    craftableCount(state): number {
      return Object.values(state.progressBySlug).filter(
        (progress) => progress.fragments >= GMAX_GUARANTEED_FRAGMENTS
      ).length
    },
  },

  actions: {
    hydrate(raw: unknown) {
      if (!raw || typeof raw !== 'object') {
        this.ensureDailyRefresh()
        return
      }

      const data = raw as GmaxRaidPersistedState
      this.particles = clampInt(data.particles, 0, GMAX_PARTICLE_CAP)
      this.lastDailyRefresh = typeof data.lastDailyRefresh === 'string' ? data.lastDailyRefresh : null
      this.selectedSlug =
        typeof data.selectedSlug === 'string' && getGmaxRaid(data.selectedSlug)
          ? data.selectedSlug
          : GMAX_RAIDS[0]?.slug ?? null
      this.progressBySlug = {}
      if (data.progressBySlug && typeof data.progressBySlug === 'object') {
        for (const [slug, progress] of Object.entries(data.progressBySlug)) {
          if (!getGmaxRaid(slug)) continue
          this.progressBySlug[slug] = normalizeProgress(progress)
        }
      }
      this.lastResult = null
      this.ensureDailyRefresh()
    },

    serialize(): GmaxRaidPersistedState {
      return {
        particles: clampInt(this.particles, 0, GMAX_PARTICLE_CAP),
        lastDailyRefresh: this.lastDailyRefresh,
        selectedSlug: this.selectedSlug,
        progressBySlug: Object.fromEntries(
          Object.entries(this.progressBySlug).map(([slug, progress]) => [
            slug,
            normalizeProgress(progress),
          ])
        ),
      }
    },

    ensureDailyRefresh(date = new Date()) {
      const key = todayKey(date)
      if (!this.lastDailyRefresh) {
        this.lastDailyRefresh = key
        this.particles = Math.min(GMAX_PARTICLE_CAP, Math.max(this.particles, GMAX_STARTING_PARTICLES))
        return
      }
      if (this.lastDailyRefresh === key) return
      this.lastDailyRefresh = key
      this.particles = Math.min(GMAX_PARTICLE_CAP, this.particles + GMAX_DAILY_PARTICLES)
    },

    getProgress(slug: string): GmaxRaidProgress {
      if (!this.progressBySlug[slug]) {
        this.progressBySlug[slug] = emptyProgress()
      }
      return this.progressBySlug[slug]!
    },

    selectRaid(slug: string) {
      if (!getGmaxRaid(slug)) return
      this.selectedSlug = slug
    },

    attemptRaid(slug: string, teamDps: number): GmaxRaidResult | null {
      this.ensureDailyRefresh()
      const raid = getGmaxRaid(slug)
      if (!raid) return null
      if (this.particles < raid.costParticles) return null

      this.particles = Math.max(0, this.particles - raid.costParticles)
      const progress = this.getProgress(slug)
      progress.attempts++

      const won = teamDps >= raid.requiredDps
      const fragmentsEarned = won ? raid.fragmentsOnWin : raid.fragmentsOnLoss
      progress.fragments = Math.min(999, progress.fragments + fragmentsEarned)

      let catchRate = 0
      let pityTriggered = false
      let fragmentsTriggered = false
      let captured = false
      let starUpgraded = false
      let alreadyMaxed = false

      if (won) {
        progress.clears++
        progress.pity++
        catchRate = estimateGmaxCatchRate(raid, teamDps, progress.pity)
        pityTriggered = progress.pity >= raid.pityRequired
        fragmentsTriggered = progress.fragments >= raid.guaranteedFragments
        captured = pityTriggered || fragmentsTriggered || Math.random() < catchRate
      }

      if (captured) {
        const inventory = useInventoryStore()
        const hasBase = inventory.collection.some(
          (p) => !p.isShiny && p.slug === raid.baseSlug,
        )
        if (!hasBase) {
          const baseName = getBasePokemonName(raid.baseSlug)
          inventory.addPokemon({
            slug: raid.baseSlug,
            nameFr: baseName.nameFr,
            nameEn: baseName.nameEn,
            stars: 1,
            isShiny: false,
            rarity: getRarity(raid.baseSlug),
            isFavorite: false,
            cosmeticFormSlug: null,
          })
        }
        starUpgraded = false
        alreadyMaxed = false
        progress.captures++
        progress.pity = 0
        if (fragmentsTriggered) {
          progress.fragments = Math.max(0, progress.fragments - raid.guaranteedFragments)
        }
      }

      const finalProgress = this.getProgress(slug)
      const raidResult: GmaxRaidResult = {
        raid,
        won,
        captured,
        catchRate,
        fragmentsEarned,
        fragments: finalProgress.fragments,
        pity: finalProgress.pity,
        pityTriggered,
        fragmentsTriggered,
        starUpgraded,
        alreadyMaxed,
        teamDps,
      }
      this.lastResult = raidResult
      return raidResult
    },

    craftGuaranteed(slug: string): GmaxRaidResult | null {
      this.ensureDailyRefresh()
      const raid = getGmaxRaid(slug)
      if (!raid) return null
      const progress = this.getProgress(slug)
      if (progress.fragments < raid.guaranteedFragments) return null

      progress.fragments = Math.max(0, progress.fragments - raid.guaranteedFragments)
      progress.pity = 0
      progress.captures++

      const inventory = useInventoryStore()
      const hasBase = inventory.collection.some((p) => !p.isShiny && p.slug === raid.baseSlug)
      if (!hasBase) {
        const baseName = getBasePokemonName(raid.baseSlug)
        inventory.addPokemon({
          slug: raid.baseSlug,
          nameFr: baseName.nameFr,
          nameEn: baseName.nameEn,
          stars: 1,
          isShiny: false,
          rarity: getRarity(raid.baseSlug),
          isFavorite: false,
          cosmeticFormSlug: null,
        })
      }

      const raidResult: GmaxRaidResult = {
        raid,
        won: true,
        captured: true,
        catchRate: 1,
        fragmentsEarned: 0,
        fragments: progress.fragments,
        pity: progress.pity,
        pityTriggered: false,
        fragmentsTriggered: true,
        starUpgraded: false,
        alreadyMaxed: false,
        teamDps: 0,
      }
      this.lastResult = raidResult
      return raidResult
    },
  },
})
